import * as React from "react"
import { Link } from "gatsby"
import "./cookie-banner.css"

const CONSENT_KEY = "zerer_cookie_consent"
const CONSENT_VERSION = "1.1"

function readConsent() {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeConsent(prefs) {
  if (typeof window === "undefined") return
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ ...prefs, version: CONSENT_VERSION, consentedAt: new Date().toISOString() })
  )
  window.dispatchEvent(new CustomEvent("zcb-consent-changed"))
}

export default function CookieBanner() {
  const [visible, setVisible] = React.useState(false)
  const [showSettings, setShowSettings] = React.useState(false)
  const [analyticsChecked, setAnalyticsChecked] = React.useState(false)
  const [linkedinChecked, setLinkedinChecked] = React.useState(false)

  React.useEffect(() => {
    window.__openCookieSettings = () => {
      setShowSettings(true)
      setVisible(true)
    }
    return () => { delete window.__openCookieSettings }
  }, [])

  React.useEffect(() => {
    const stored = readConsent()
    if (!stored || stored.version !== CONSENT_VERSION) {
      if (stored) {
        setAnalyticsChecked(stored.analytics === true)
        setLinkedinChecked(stored.linkedin === true)
      }
      setVisible(true)
    }
  }, [])

  function acceptAll() {
    writeConsent({ necessary: true, analytics: true, linkedin: true, decision: "all" })
    setVisible(false)
    if (typeof window !== "undefined" && window.__activateAnalytics) {
      window.__activateAnalytics()
    }
  }

  function acceptNecessaryOnly() {
    writeConsent({ necessary: true, analytics: false, linkedin: false, decision: "necessary" })
    setVisible(false)
  }

  function saveSettings() {
    writeConsent({ necessary: true, analytics: analyticsChecked, linkedin: linkedinChecked, decision: "custom" })
    setVisible(false)
    if (analyticsChecked && typeof window !== "undefined" && window.__activateAnalytics) {
      window.__activateAnalytics()
    }
  }

  if (!visible) return null

  return (
    <div className="zcb-overlay" role="dialog" aria-modal="true" aria-label="Cookie-Einstellungen">
      <div className="zcb-banner">
        {!showSettings ? (
          <div className="zcb-main">
            <div className="zcb-text">
              <strong>Diese Website verwendet Cookies</strong>
              <p>
                Technisch notwendige Cookies sind immer aktiv. Mit Ihrer Einwilligung setzen wir
                optionale Cookies für Analyse (Google Analytics) und externe Inhalte (LinkedIn).{" "}
                <Link to="/datenschutz" className="zcb-link">
                  Datenschutzerklärung
                </Link>
              </p>
            </div>
            <div className="zcb-actions">
              <button className="zcb-btn zcb-btn-primary" onClick={acceptAll}>
                Alle akzeptieren
              </button>
              <button className="zcb-btn zcb-btn-secondary" onClick={acceptNecessaryOnly}>
                Nur notwendige
              </button>
              <button className="zcb-btn zcb-btn-link" onClick={() => setShowSettings(true)}>
                Einstellungen
              </button>
            </div>
          </div>
        ) : (
          <div className="zcb-settings">
            <h6 className="zcb-settings-title">Cookie-Einstellungen</h6>

            <div className="zcb-category">
              <div className="zcb-category-header">
                <div>
                  <span className="zcb-category-name">Technisch notwendige Cookies</span>
                  <p className="zcb-category-desc">
                    Erforderlich für die grundlegende Funktion der Website. Können nicht deaktiviert werden.
                  </p>
                </div>
                <span className="zcb-always-active">Immer aktiv</span>
              </div>
            </div>

            <div className="zcb-divider" />

            <div className="zcb-category">
              <div className="zcb-category-header">
                <div>
                  <span className="zcb-category-name">LinkedIn</span>
                  <p className="zcb-category-desc">
                    Ermöglicht die Anzeige von LinkedIn-Beiträgen. LinkedIn kann dabei Daten zu
                    Ihrem Nutzungsverhalten erheben. Nur mit Ihrer ausdrücklichen Einwilligung aktiv.
                  </p>
                </div>
                <label className="zcb-toggle" aria-label="LinkedIn-Cookies aktivieren">
                  <input
                    type="checkbox"
                    checked={linkedinChecked}
                    onChange={(e) => setLinkedinChecked(e.target.checked)}
                  />
                  <span className="zcb-toggle-track">
                    <span className="zcb-toggle-thumb" />
                  </span>
                </label>
              </div>
            </div>

            <div className="zcb-divider" />

            <div className="zcb-category">
              <div className="zcb-category-header">
                <div>
                  <span className="zcb-category-name">Analyse & Statistik</span>
                  <p className="zcb-category-desc">
                    Google Analytics – hilft uns zu verstehen, wie Besucher die Website nutzen.
                    Daten werden anonym ausgewertet. Nur mit Ihrer ausdrücklichen Einwilligung aktiv.
                  </p>
                </div>
                <label className="zcb-toggle" aria-label="Analyse-Cookies aktivieren">
                  <input
                    type="checkbox"
                    checked={analyticsChecked}
                    onChange={(e) => setAnalyticsChecked(e.target.checked)}
                  />
                  <span className="zcb-toggle-track">
                    <span className="zcb-toggle-thumb" />
                  </span>
                </label>
              </div>
            </div>

            <div className="zcb-settings-actions">
              <button className="zcb-btn zcb-btn-primary" onClick={saveSettings}>
                Auswahl speichern
              </button>
              <button className="zcb-btn zcb-btn-secondary" onClick={acceptAll}>
                Alle akzeptieren
              </button>
              <button className="zcb-btn zcb-btn-link" onClick={() => setShowSettings(false)}>
                Zurück
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function getCookieConsent() {
  return readConsent()
}

export function hasAnalyticsConsent() {
  const c = readConsent()
  return c !== null && c.analytics === true
}

export function hasLinkedInConsent() {
  const c = readConsent()
  return c !== null && c.linkedin === true
}
